import React, { useRef, useState } from "react";
import { User, Building2, CheckCircle2, ArrowUpRight, AlertCircle } from "lucide-react";
import Seo, { breadcrumbJsonLd } from "@/components/Seo";
import { Container } from "@/components/ui-kit/Section";
import { Reveal } from "@/components/motion/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Cta from "@/components/ui-kit/Cta";
import { joinWaitlist } from "@/lib/api";
import { trackFormSubmit } from "@/lib/analytics";
import { LIVE_APP_URL } from "@/lib/site";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TYPES = [
  { key: "individual", icon: User, title: "Individual", body: "A freelancer, gig worker, or 1099 contractor setting aside your own taxes." },
  { key: "agency", icon: Building2, title: "Agency or accountant", body: "A firm managing set asides for many clients and earning commission." },
];

export default function GetStarted() {
  const [accountType, setAccountType] = useState("individual");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const honeypot = useRef("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      setError("Enter a valid email address.");
      return;
    }
    setStatus("loading");
    try {
      await joinWaitlist({ email: value, accountType, source: "get-started", honeypot: honeypot.current });
      trackFormSubmit("waitlist");
      setStatus("done");
      setError("");
      setEmail("");
    } catch {
      setStatus("error");
      setError("Something went wrong. Your details are safe. Please try again.");
    }
  };

  return (
    <>
      <Seo
        title="Get Started with ETAP | Individual or Agency"
        description="Choose your path and join early access. Individuals set aside their own taxes. Agencies manage clients and earn commission. Read only, non custodial."
        path="/get-started"
        jsonLd={[
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Get Started", path: "/get-started" },
          ]),
        ]}
      />

      <section className="relative overflow-hidden pt-32 pb-20 sm:pt-36 sm:pb-28">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(55% 45% at 50% 0%, rgba(197,254,237,0.55) 0%, rgba(197,254,237,0) 60%), linear-gradient(180deg, rgba(238,243,248,0.7) 0%, rgba(248,250,252,0) 60%)",
          }}
        />
        <Container>
          <div className="mx-auto max-w-2xl">
            {status === "done" ? (
              <Reveal className="rounded-3xl border border-etap-mint-300 bg-white p-8 text-center shadow-etap-lg sm:p-10" data-testid="waitlist-success">
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-etap-mint-200 text-etap-deep-700">
                  <CheckCircle2 className="h-8 w-8" aria-hidden="true" />
                </span>
                <h1 className="mt-5 font-display text-3xl font-bold tracking-tight text-etap-deep-900">You are on the list</h1>
                <p className="mx-auto mt-3 max-w-md text-etap-deep-600">
                  We will email you when your account is ready. If you already have access, you can log in to the app now.
                </p>
                <div className="mt-7 flex flex-wrap justify-center gap-3">
                  <Cta href={LIVE_APP_URL} variant="primary" location="waitlist-login" testId="waitlist-open-app">
                    Open the app
                    <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                  </Cta>
                  <Cta to="/how-it-works" variant="outline" location="waitlist-how" testId="waitlist-how">
                    See how it works
                  </Cta>
                </div>
              </Reveal>
            ) : (
              <>
                <Reveal className="text-center">
                  <h1 className="font-display text-[clamp(2.25rem,4.6vw,3.5rem)] font-extrabold tracking-tight text-etap-deep-900">
                    Start free setup
                  </h1>
                  <p className="mx-auto mt-4 max-w-md text-lg text-etap-deep-600">
                    Pick how you will use ETAP, then join early access. It takes one field.
                  </p>
                </Reveal>

                <Reveal delay={0.1} className="mt-9">
                  <fieldset>
                    <legend className="sr-only">Choose your account type</legend>
                    <div className="grid gap-4 sm:grid-cols-2">
                      {TYPES.map((t) => {
                        const active = accountType === t.key;
                        return (
                          <button
                            key={t.key}
                            type="button"
                            onClick={() => setAccountType(t.key)}
                            aria-pressed={active}
                            data-testid={`type-${t.key}`}
                            className={`rounded-3xl border p-6 text-left transition-[border-color,box-shadow,background-color] duration-200 ${
                              active
                                ? "border-etap-primary bg-etap-primary-50 shadow-etap-md"
                                : "border-etap-deep-100 bg-white hover:border-etap-primary/50"
                            }`}
                          >
                            <span
                              className={`flex h-11 w-11 items-center justify-center rounded-xl ${
                                active ? "bg-etap-primary text-white" : "bg-etap-primary-50 text-etap-primary"
                              }`}
                            >
                              <t.icon className="h-5 w-5" aria-hidden="true" />
                            </span>
                            <p className="mt-4 font-display text-lg font-bold text-etap-deep-800">{t.title}</p>
                            <p className="mt-1 text-sm text-etap-deep-600">{t.body}</p>
                          </button>
                        );
                      })}
                    </div>
                  </fieldset>
                </Reveal>

                <Reveal delay={0.15} className="mt-6">
                  <form onSubmit={submit} className="rounded-3xl border border-etap-deep-100 bg-white p-6 shadow-etap-lg sm:p-8" noValidate>
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      className="hidden"
                      onChange={(e) => {
                        honeypot.current = e.target.value;
                      }}
                    />
                    <Label htmlFor="waitlist-email" className="text-etap-deep-700">Email address</Label>
                    <Input
                      id="waitlist-email"
                      type="email"
                      value={email}
                      maxLength={140}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="mt-2 h-12 rounded-xl border-etap-deep-100"
                      data-testid="waitlist-email"
                    />
                    {error && (
                      <p className="mt-2 flex items-center gap-1.5 text-sm text-red-600" data-testid="waitlist-error">
                        <AlertCircle className="h-4 w-4" />
                        {error}
                      </p>
                    )}
                    <div className="mt-5">
                      <Cta onClick={submit} variant="primary" size="lg" className="w-full" testId="waitlist-submit">
                        {status === "loading" ? "Joining..." : "Join early access"}
                      </Cta>
                    </div>
                    <p className="mt-4 text-center text-sm text-etap-deep-500">
                      Already have an account?{" "}
                      <a
                        href={LIVE_APP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-etap-primary underline underline-offset-2"
                        data-testid="waitlist-login-link"
                      >
                        Log in
                      </a>
                    </p>
                  </form>
                </Reveal>
              </>
            )}
          </div>
        </Container>
      </section>
    </>
  );
}
